/**
 * Route table for the Fossil Foolish static site.
 *
 * Every route is lazy-loaded via `React.lazy(() => import(...))` so that
 * this file can compile in the site-foundation worktree before the
 * cross-squad route modules (leaderboard, receipts, retracted, legal)
 * have landed on disk. TypeScript does not eagerly resolve dynamic
 * `import()` paths at type-check time; the real module resolution only
 * happens when vite-ssg walks the router during the build, by which
 * point all four worktrees will have merged.
 *
 * Layout wrapping (Header, Footer, SatireNotice, TakedownContact) is
 * owned by the site/layout-legal squad and composed into this route
 * tree on merge. Lazy imports are intentionally bare so each squad
 * can land its own routes without touching `App.tsx`.
 */
import { lazy, Suspense, useEffect } from 'react';
import type { ReactElement } from 'react';
import { Outlet, useLocation, type RouteObject } from 'react-router-dom';

// Cross-squad route modules. These files do not yet exist in the
// site-foundation worktree; they land in parallel worktrees and merge
// cleanly because React.lazy defers resolution to build time.
const LeaderboardRoute = lazy(() => import('./routes/index'));
const BankerLeaderboardRoute = lazy(() => import('./routes/bankers/index'));
const BankerReceiptRoute = lazy(() => import('./routes/bankers/[slug]'));
const ReceiptRoute = lazy(() => import('./routes/receipts/slug'));
const RetractedRoute = lazy(() => import('./routes/retracted/slug'));
const PrivacyRoute = lazy(() => import('./routes/legal/privacy'));
const TermsRoute = lazy(() => import('./routes/legal/terms'));
const NotFoundRoute = lazy(() => import('./routes/not-found'));

function RootLayout(): ReactElement {
  const { pathname } = useLocation();
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
  }, [pathname]);
  return <Outlet />;
}

function withSuspense(element: ReactElement): ReactElement {
  // Suspense fallback is deliberately empty — vite-ssg pre-renders every
  // route, so visitors never see the hydration boundary. The boundary
  // exists only to satisfy `React.lazy`'s type contract.
  return <Suspense fallback={null}>{element}</Suspense>;
}

export const routes: RouteObject[] = [
  {
    element: <RootLayout />,
    children: [
      {
        path: '/',
        element: withSuspense(<LeaderboardRoute />),
      },
      {
        path: '/bankers',
        element: withSuspense(<BankerLeaderboardRoute />),
      },
      {
        path: '/bankers/:slug',
        element: withSuspense(<BankerReceiptRoute />),
      },
      {
        path: '/receipts/:slug',
        element: withSuspense(<ReceiptRoute />),
      },
      {
        path: '/retracted/:slug',
        element: withSuspense(<RetractedRoute />),
      },
      {
        path: '/legal/privacy',
        element: withSuspense(<PrivacyRoute />),
      },
      {
        path: '/legal/terms',
        element: withSuspense(<TermsRoute />),
      },
      {
        path: '/404',
        element: withSuspense(<NotFoundRoute />),
      },
      {
        path: '*',
        element: withSuspense(<NotFoundRoute />),
      },
    ],
  },
];

